/**
 * Bitta so'rovning holatini boshqaruvchi hook.
 *
 * Kabinetdagi har bir panel o'z resursiga ega: yuklanmoqda → xato → bo'sh →
 * ma'lumot. Bitta endpoint ishlamasa qolganlari ishlashda davom etadi, huquq
 * yetmasa (403) panel shu haqda aniq yozadi.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError } from "@/shared/api/client";
import { useI18n } from "@/shared/i18n";

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  /** Bo'sh bo'lmasa — foydalanuvchiga ko'rsatiladigan xato matni. */
  error: string;
}

/** Xatoni foydalanuvchi tiliga o'giradi (huquq, aloqa, boshqa). */
export function errorMessage(error: unknown, noAccess: string, offline: string): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return offline;
    if (error.status === 403) return noAccess;
    return error.message;
  }
  return offline;
}

/**
 * `deps` o'zgarganda so'rov qaytadan yuboriladi (masalan davr yoki sahifa
 * almashganda). `reload()` — «qayta urinish» tugmasi uchun.
 */
export function useResource<T>(
  loader: () => Promise<T>,
  deps: unknown[],
): Resource<T> & { reload: () => void } {
  const { t } = useI18n();
  const [state, setState] = useState<Resource<T>>({ data: null, loading: true, error: "" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    loader()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: "" });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          data: null,
          loading: false,
          error: errorMessage(error, t("state.noAccess"), t("auth.err.network")),
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return { ...state, reload };
}
